import { inject, injectable } from 'inversify';

import { History } from '@vk-hr-tek/core/history';
import { RoleService } from '@vk-hr-tek/core/http';

import { Widget as WidgetGen } from '@app/gen/dashboard';

import { SettingsRouter } from '../../../settings/types';
import { modules } from '../../modules';

@injectable()
export class SettingsRouterService implements SettingsRouter {
  constructor(
    @inject(History) private history: History,
    @inject(RoleService) private readonly role: RoleService,
  ) {}

  goToMainSettings(companyId?: string, eventTypeId?: string) {
    const query = new URLSearchParams();

    if (companyId) {
      query.set('companyId', companyId);
    }

    if (eventTypeId) {
      query.set('eventTypeId', eventTypeId);
    }

    const search = query.toString();

    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/event-types${
        search ? `?${search}` : ''
      }`,
    );
  }

  goToCompanyStructure(id: string) {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }/${id}/structure`,
    );
  }

  goToCompanyProfilesEdit(id: string) {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }/${id}/profiles/edit`,
    );
  }

  goBack(prevState: { pathname: string; search: string }) {
    this.history.push(prevState);
  }

  goToCompanySettings() {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }`,
    );
  }

  goToCompanyAccessProfiles(id: string) {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }/${id}/access-profiles`,
    );
  }

  goToCompanyAccessProfilesSetup(id: string) {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }/${id}/access-profiles/setup`,
    );
  }

  goToEditCompanyTimezone(id: string) {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }/${id}/timezone/edit`,
    );
  }

  goToCreateCompanyTimezoneException(id: string) {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }/${id}/timezone/exception/create`,
    );
  }

  goToCompanyTimezone(id: string) {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }/${id}/timezone`,
    );
  }

  goToCompanyDashboardSettings(id: string) {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }/${id}/dashboard-settings`,
    );
  }

  goToCompanyDashboard(id: string) {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }/${id}/dashboard`,
    );
  }

  goToCompanyWidget(
    id: string,
    widgetId: string,
    widgetType: WidgetGen['type'],
  ) {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }/${id}/dashboard/${widgetId}/${widgetType}`,
    );
  }

  goToEventTypes(id: string, collectionId: string) {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }/${id}/collections/${collectionId}`,
    );
  }

  goToCompanyLogoSettings(id: string) {
    this.history.push(
      `/${this.role.get()}/${modules.settings.basename}/${
        modules.company.basename
      }/${id}/logo`,
    );
  }
}
